import { Constants, isUkcRallyLevel, isUkcRallyShow, ukcRallyClassMatches } from "@eagerdog/constants";

import { IShow, IShowElement } from "@eagerdog/interfaces";

import { IJudge } from "../JudgesTab/JudgesTab";
import { IPrice } from "../PriceTab/PriceTab";

// TODO: decompose

interface INestedShowLevel {
  level: string
  levelSelected: boolean
  sections?: {
    section: string, sectionSelected: boolean
  }[]
}

interface INestedShowElement {
  element: string
  elementSelected: boolean
  showLevels: INestedShowLevel[]
  elementSections?: { section: string, sectionSelected: boolean }[]
}

const nestedElementSetupShows = [
  Constants.show_type.herding,
  Constants.show_type.pre_trial_herding,
  Constants.show_type.instinct_test_herding,
  Constants.show_type.obedience, // C-WAGS Obedience has sections
  Constants.show_type.rally_obedience, // C-WAGS Rally has sections
  Constants.show_type.scent, // C-WAGS Scent has nested elements
  Constants.show_type.cwags_games, // C-WAGS Games: game (element) → level, no sections
  Constants.show_type.urban_rat_race, // ABI Urban Rat Race has nested elements with sections for some elements
  Constants.show_type.lost_and_found, // ABI Lost and Found has nested elements with sections
  Constants.show_type.field_trial, // ASFA Field Trial should toggle individual levels
  // NOTE: UKC Nosework is NOT nested - it displays as simple checkboxes (Container, Exterior, etc)
  // NOTE: ISWS Conformation is handled separately in conditional logic (not in this array to avoid breaking AKC/UKC Conformation)
];

// Check if a show should use nested element setup (considering sanctioning club)
const shouldUseNestedSetup = (showType: string, sanctioningClub: string): boolean => {
  // ISWS and CKC Conformation use nested setup (element → class level)
  if (showType === Constants.show_type.conformation &&
      (sanctioningClub === Constants.sanctioning_club.ISWS || sanctioningClub === Constants.sanctioning_club.CKC)) {
    return true;
  }

  // UKC Nosework uses nested setup (element → level, sections are added per-entry at registration)
  if (showType === Constants.show_type.nosework && sanctioningClub === Constants.sanctioning_club.UKC) {
    return true;
  }

  // UKC Rally uses nested setup (Rally Obedience → RO1/RO2/RO3/ROM → A/B/C, URX/RMX without sections)
  if (isUkcRallyShow(sanctioningClub, showType)) {
    return true;
  }

  // AKC Scentwork uses nested setup (element → level)
  if (showType === Constants.show_type.scentwork && sanctioningClub === Constants.sanctioning_club.AKC) {
    return true;
  }

  // CKC Scent Detection uses nested setup (component → class)
  if (showType === Constants.show_type.scent_detection && sanctioningClub === Constants.sanctioning_club.CKC) {
    return true;
  }

  // ABI Scent Dog Games uses nested setup (game → Level 1 / Level 2 sections)
  if (showType === Constants.show_type.scent_dog_games && sanctioningClub === Constants.sanctioning_club.ABI) {
    return true;
  }

  // 4-H Agility uses nested setup (element → On-Leash / Off-Leash sections)
  if (showType === Constants.show_type.agility && sanctioningClub === Constants.sanctioning_club.FH) {
    return true;
  }
  
  // C-WAGS Obedience/Rally/Scent use nested setup
  if (nestedElementSetupShows.includes(showType) && sanctioningClub === Constants.sanctioning_club.C_WAGS) {
    return true;
  }
  
  // AHBA Herding uses nested setup (element → level + stock section)
  if (showType === Constants.show_type.herding && sanctioningClub === Constants.sanctioning_club.AHBA) {
    return true;
  }

  // Other shows in the nestedElementSetupShows array (Herding, etc.) - but not Obedience/Rally
  if (nestedElementSetupShows.includes(showType) && 
      showType !== Constants.show_type.obedience && 
      showType !== Constants.show_type.rally_obedience) {
    return true;
  }
  
  return false;
};

const showSectionAssociation = {
  [Constants.show_type.herding]: [
    Constants.dog_class_section.sheep,
    Constants.dog_class_section.cattle,
    Constants.dog_class_section.ducks,
  ],
  // AKC Herding uses the same sections as generic herding
  [`${Constants.sanctioning_club.AKC}_${Constants.show_type.herding}`]: [
    Constants.dog_class_section.sheep,
    Constants.dog_class_section.cattle,
    Constants.dog_class_section.ducks,
  ],
  // AHBA Herding uses an expanded set of stock types
  [`${Constants.sanctioning_club.AHBA}_${Constants.show_type.herding}`]: [
    Constants.dog_class_section.sheep,
    Constants.dog_class_section.cattle,
    Constants.dog_class_section.ducks,
    Constants.dog_class_section.goats,
    Constants.dog_class_section.geese,
    Constants.dog_class_section.turkeys,
    Constants.dog_class_section.mixed_stock,
  ],
  // ABI Urban Rat Race - only Tubes, Unobstructed, Obstructed, and Obstructed 2 have sections (not Games)
  [`${Constants.sanctioning_club.ABI}_${Constants.show_type.urban_rat_race}`]: [
    Constants.dog_class_section.on_leash,
    Constants.dog_class_section.unleashed,
  ],
  // ABI Lost and Found - all three elements (Containers, Interiors, Exteriors) have on/off leash sections
  [`${Constants.sanctioning_club.ABI}_${Constants.show_type.lost_and_found}`]: [
    Constants.dog_class_section.on_leash,
    Constants.dog_class_section.unleashed,
  ],
  // ABI Scent Dog Games - Level 1 and Level 2 sections appear under each game
  [`${Constants.sanctioning_club.ABI}_${Constants.show_type.scent_dog_games}`]: [
    Constants.dog_class_section.sdg_level_1,
    Constants.dog_class_section.sdg_level_2,
  ],
  // 4-H Agility - Division A and Division B sections
  [`${Constants.sanctioning_club.FH}_${Constants.show_type.agility}`]: [
    Constants.dog_class_section.fh_agility_division_a,
    Constants.dog_class_section.fh_agility_division_b,
  ],
  // NOTE: UKC Nosework sections (A, B) are NOT shown during show setup
  // They are added during registration in FormBuilder as a user selection
  // ONLY C-WAGS Obedience and Rally have sections during show setup
  // C-WAGS Obedience and Rally use sections A, B, TO, JR + FEO (For Exhibition Only).
  // FEO runs are priced with the flat show.feo_fee (Price tab), not per section.
  [`${Constants.sanctioning_club.C_WAGS}_${Constants.show_type.obedience}`]: [
    Constants.dog_class_section.cwags_obedience_a,
    Constants.dog_class_section.cwags_obedience_b,
    Constants.dog_class_section.cwags_obedience_to,
    Constants.dog_class_section.cwags_obedience_jr,
    Constants.dog_class_section.feo,
  ],
  [`${Constants.sanctioning_club.C_WAGS}_${Constants.show_type.rally_obedience}`]: [
    Constants.dog_class_section.cwags_rally_a,
    Constants.dog_class_section.cwags_rally_b,
    Constants.dog_class_section.cwags_rally_to,
    Constants.dog_class_section.cwags_rally_jr,
    Constants.dog_class_section.feo,
  ],
}

// Convert nested setup struct to plain show.show_elements struct
const getShowElementsByNested = (
  showElementsNested: INestedShowElement[],
  // judges: IJudge[]
  judges?: IJudge[],
  prices?: IPrice[],
  show?: IShow
): IShowElement[] => {
  const _showElements: IShowElement[] = [];
  const existingShowElements = show?.show_elements;

  const getExistingElementData = (
    element: string,
    level: string,
    section: string
  ) => {
    return existingShowElements?.find((se) => (
      se.show_element === element &&
      se.level === level &&
      (se.section || '') === (section || '')
    ));
  }

  // For older shows stored in flat format (no level field), fall back to matching
  // by show_element only so judge info carries over when adding new levels.
  const getExistingElementFallback = (element: string) => {
    return existingShowElements?.find((se) => se.show_element === element && !se.level);
  }

  const getJudgeInfo = (
    element: string,
    level: string,
    section: string
  ): IJudge | undefined => {
    return judges?.find((j) => (j.judge_element === element && j.judge_level === level && j.judge_section === section));
  }

  const getPriceInfo = (
    element: string,
    level: string,
    section: string
  ): IPrice | undefined => {
    return prices?.find((j) => (j.judge_element === element && j.judge_level === level && j.judge_section === section));
  }

  showElementsNested.forEach((n) => {
    // Handle element-level sections (no intermediate level tier, e.g. ABI Scent Dog Games)
    if (n.elementSections && n.elementSections.length > 0) {
      if (n.elementSelected) {
        n.elementSections.forEach((section) => {
          if (section.sectionSelected) {
            const exEl = getExistingElementData(n.element, '', section.section)
                      ?? getExistingElementFallback(n.element);

            let judgeInfo: { judge_name: string, judge_number?: string, backup_judge_name?: string, backup_judge_number?: string } = {
              judge_name: exEl?.judge_name ?? "",
              judge_number: exEl?.judge_number ?? "",
              backup_judge_name: exEl?.backup_judge_name ?? "",
              backup_judge_number: exEl?.backup_judge_number ?? ""
            };
            let priceInfo: { fee: number, early_bird_fee?: number } = { fee: exEl?.fee ?? 0 };

            if (judges?.length) {
              if (judges.length > 1) {
                const judge = getJudgeInfo(n.element, '', section.section);
                if (judge) {
                  const { judge_name, judge_number, backup_judge_name, backup_judge_number } = judge;
                  judgeInfo = { judge_name, judge_number, backup_judge_name, backup_judge_number };
                }
              } else {
                judgeInfo.judge_name = judges[0]?.judge_name ?? "N/A";
                judgeInfo.judge_number = judges[0]?.judge_number ?? "N/A";
                judgeInfo.backup_judge_name = judges[0]?.backup_judge_name ?? "";
                judgeInfo.backup_judge_number = judges[0]?.backup_judge_number ?? "";
              }
            }

            if (prices?.length) {
              if (prices.length > 1) {
                const price = getPriceInfo(n.element, '', section.section);
                if (price) {
                  const { fee, early_bird_fee } = price;
                  priceInfo.fee = Number(fee);
                  if (early_bird_fee) priceInfo.early_bird_fee = Number(early_bird_fee);
                }
              } else {
                priceInfo.fee = prices[0]?.fee ? Number(prices[0].fee) : 0;
                if (prices[0]?.early_bird_fee) priceInfo.early_bird_fee = Number(prices[0]?.early_bird_fee);
              }
            }

            _showElements.push({
              show_element: n.element,
              section: section.section,
              ...judgeInfo,
              ...priceInfo,
              max_entries: exEl?.max_entries ?? 500,
              waitlist_enabled: exEl?.waitlist_enabled ?? false,
            });
          }
        });
      }
      return; // skip level-based processing for this element
    }

    // check if at least one level is selected (with or without sections)
    const hasSelectedLevel = n.showLevels.some(level => {
      if (level.sections && level.sections.length > 0) {
        // Show with sections - check if level AND section are selected
        return level.levelSelected && level.sections.some(section => section.sectionSelected);
      } else {
        // Show without sections - just check if level is selected
        return level.levelSelected;
      }
    });

    if (hasSelectedLevel) {
      n.showLevels.forEach((level) => {
        if (level.levelSelected) {
          if (level.sections && level.sections.length > 0) {
            // Show WITH sections (UKC Nosework, C-WAGS Obedience/Rally)
            level.sections.forEach((section) => {
              const exEl = getExistingElementData(n.element, level.level, section.section)
                        ?? getExistingElementFallback(n.element);

              let judgeInfo: { judge_name: string, judge_number?: string, backup_judge_name?: string, backup_judge_number?: string } = { 
                judge_name: exEl?.judge_name ?? "", 
                judge_number: exEl?.judge_number ?? "",
                backup_judge_name: exEl?.backup_judge_name ?? "",
                backup_judge_number: exEl?.backup_judge_number ?? ""
              };
              let priceInfo: { fee: number, early_bird_fee?: number } = { fee: exEl?.fee ?? 0 };

              if (judges?.length) {
                if (judges.length > 1) { // multi judges
                  const judge = getJudgeInfo(n.element, level.level, section.section);
                  if (judge) {
                    const { judge_name, judge_number, backup_judge_name, backup_judge_number } = judge;
                    judgeInfo = { judge_name, judge_number, backup_judge_name, backup_judge_number };
                  }
                } else { // single judge
                  judgeInfo.judge_name = judges[0]?.judge_name ?? "N/A";
                  judgeInfo.judge_number = judges[0]?.judge_number ?? "N/A";
                  judgeInfo.backup_judge_name = judges[0]?.backup_judge_name ?? "";
                  judgeInfo.backup_judge_number = judges[0]?.backup_judge_number ?? "";
                }
              }

              if (prices?.length) {
                if (prices.length > 1) { // multi prices
                  const price = getPriceInfo(n.element, level.level, section.section);
                  if (price) {
                    const { fee, early_bird_fee } = price;
                    priceInfo.fee = Number(fee);
                    if (early_bird_fee) priceInfo.early_bird_fee = Number(early_bird_fee);
                  }
                } else { // single price
                  priceInfo.fee = prices[0]?.fee ? Number(prices[0].fee) : 0;
                  if (prices[0]?.early_bird_fee) priceInfo.early_bird_fee = Number(prices[0]?.early_bird_fee);
                }
              }

              if (section.sectionSelected) {
                _showElements.push({
                  show_element: n.element,
                  level: level.level,
                  section: section.section,

                  ...judgeInfo,
                  ...priceInfo,
                  // Preserve explicitly-configured limit data; do NOT auto-default max_entries to 500
                  // for section rows (herding stock limits) — undefined means unlimited.
                  // The WaitlistTab's Per Stock Limit UI manages these explicitly.
                  ...(exEl?.max_entries !== undefined ? { max_entries: exEl.max_entries } : {}),
                  ...(exEl?.waitlist_enabled ? { waitlist_enabled: exEl.waitlist_enabled } : {}),
                  ...(exEl?.waitlist_size ? { waitlist_size: exEl.waitlist_size } : {}),
                });
              }
            });
          } else {
            // Show WITHOUT sections (C-WAGS Scent, AKC Scentwork levels)
            const exEl = getExistingElementData(n.element, level.level, '')
                      ?? getExistingElementFallback(n.element);

            let judgeInfo: { judge_name: string, judge_number?: string, backup_judge_name?: string, backup_judge_number?: string } = { 
              judge_name: exEl?.judge_name ?? "", 
              judge_number: exEl?.judge_number ?? "",
              backup_judge_name: exEl?.backup_judge_name ?? "",
              backup_judge_number: exEl?.backup_judge_number ?? ""
            };
            let priceInfo: { fee: number, early_bird_fee?: number } = { fee: exEl?.fee ?? 0 };

            if (judges?.length) {
              if (judges.length > 1) {
                // For multi judges without sections, match by element + level only
                const judge = judges.find((j) => (j.judge_element === n.element && j.judge_level === level.level));
                if (judge) {
                  const { judge_name, judge_number, backup_judge_name, backup_judge_number } = judge;
                  judgeInfo = { judge_name, judge_number, backup_judge_name, backup_judge_number };
                }
              } else {
                // Single judge
                judgeInfo.judge_name = judges[0]?.judge_name ?? "N/A";
                judgeInfo.judge_number = judges[0]?.judge_number ?? "N/A";
                judgeInfo.backup_judge_name = judges[0]?.backup_judge_name ?? "";
                judgeInfo.backup_judge_number = judges[0]?.backup_judge_number ?? "";
              }
            }

            if (prices?.length) {
              if (prices.length > 1) {
                const price = prices.find((p) => (p.judge_element === n.element && p.judge_level === level.level));
                if (price) {
                  const { fee, early_bird_fee } = price;
                  priceInfo.fee = Number(fee);
                  if (early_bird_fee) priceInfo.early_bird_fee = Number(early_bird_fee);
                }
              } else {
                priceInfo.fee = prices[0]?.fee ? Number(prices[0].fee) : 0;
                if (prices[0]?.early_bird_fee) priceInfo.early_bird_fee = Number(prices[0]?.early_bird_fee);
              }
            }

            _showElements.push({
              show_element: n.element,
              level: level.level,

              ...judgeInfo,
              ...priceInfo,
              max_entries: exEl?.max_entries ?? 500,
              waitlist_enabled: exEl?.waitlist_enabled ?? false,
            });
          }
        }
      });
    }
  });

  return _showElements;
}

// Provides selection state for nested elements build function in ShowType tab
const getElementSelectionState = (
  showElements: IShowElement[],
  element: string,
  level?: string,
  section?: string
): boolean => {
  return showElements.some((se) => {
    if (se.show_element !== element && se.level !== element) return false;
    // UKC Rally: a saved "RO1 B" (legacy) and a saved RO1 + B (canonical) are the same class
    if (level !== undefined && isUkcRallyLevel(level) && isUkcRallyLevel(se.level)) {
      return ukcRallyClassMatches(se, { level, section });
    }
    return (
      // For flat show types (AKC Obedience), check se.level === element
      // For nested show types (C-WAGS Obedience), check se.show_element === element
      (level === undefined || se.level === level) &&
      (section === undefined || se.section === section)
    );
  });
};

export { type INestedShowElement, nestedElementSetupShows, shouldUseNestedSetup, showSectionAssociation, getShowElementsByNested, getElementSelectionState }

// Shows that display sections during show setup (in ShowTypeTab)
// UKC Nosework is NOT here because sections are added during registration, not show setup
export const showsWithSectionsInSetup = [
  Constants.show_type.herding,
  Constants.show_type.obedience, // C-WAGS only
  Constants.show_type.rally_obedience, // C-WAGS and UKC
];
